import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from './MainLayout';
import CustomSelect from './CustomSelect';
import { SortablePaginatedTable } from './common';
import ContractAssignmentModal from './ContractAssignmentModal';
import { Grid, Button, Box } from '@mui/material';
import '../styles/InvitationsPage.css'; // Keep for modal styles
import '../styles/Contracts.css'; // Add contracts styling

// Column definitions for the users table
const userColumns = [
  { 
    id: 'email', 
    label: 'Email', 
    sortable: true 
  },
  { 
    id: 'status', 
    label: 'Status', 
    sortable: true,
    render: (row) => (
      <span className={`status-pill ${row.status}`}>
        {row.status}
      </span>
    )
  },
  { 
    id: 'role', 
    label: 'Role', 
    sortable: true,
    render: (row) => row.role?.charAt(0).toUpperCase() + row.role?.slice(1)
  },
  { 
    id: 'createdDate', 
    label: 'Created/Invited', 
    sortable: true,
    getValue: (row) => row.createdDate ? new Date(row.createdDate).getTime() : 0,
    render: (row) => row.createdDate || 'N/A'
  },
  { 
    id: 'updatedDate', 
    label: 'Last Updated', 
    sortable: true,
    getValue: (row) => row.updatedDate ? new Date(row.updatedDate).getTime() : 0,
    render: (row) => row.updatedDate || 'N/A'
  },
  { 
    id: 'lastLogin', 
    label: 'Last Login', 
    sortable: true,
    getValue: (row) => row.lastLogin ? new Date(row.lastLogin).getTime() : 0,
    render: (row) => row.lastLogin || 'N/A'
  },
  { 
    id: 'actions', 
    label: 'Actions', 
    sortable: false,
    render: (row) => row.actionsComponent
  }
];

export const UsersPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showContractsModal, setShowContractsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [allContracts, setAllContracts] = useState([]);
  const [userContracts, setUserContracts] = useState([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [newInvitation, setNewInvitation] = useState({
    email: '',
    role: 'client' // Default role - options match user_role enum: admin, client, team_member
  });
  const [roleUpdate, setRoleUpdate] = useState({
    role: 'client'
  });
  const [statusUpdate, setStatusUpdate] = useState({
    status: 'active'
  });
  const [filters, setFilters] = useState({
    role: 'all',
    status: 'all'
  });

  // New state for invitation contract selection
  const [inviteContracts, setInviteContracts] = useState([]);
  const [inviteContractsLoading, setInviteContractsLoading] = useState(false);
  const [selectedInviteContractIds, setSelectedInviteContractIds] = useState(new Set());
  const [inviteContractSearchTerm, setInviteContractSearchTerm] = useState('');

  // Available filter options
  const roleOptions = [
    { value: 'all', label: 'All Roles' },
    { value: 'admin', label: 'Admin' },
    { value: 'client', label: 'Client' },
    { value: 'team_member', label: 'Team Member' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'invited', label: 'Invited' },
    { value: 'disabled', label: 'Disabled' }
  ];

  // Fetch users from the API
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
      const response = await fetch(`${apiBaseUrl}/api/users`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.status}`);
      }
      
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Load users on mount
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Handle filter changes
  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Fetch all contracts for the contract assignment modal
  const fetchAllContracts = async () => {
    try {
      setContractsLoading(true);
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
      const response = await fetch(`${apiBaseUrl}/api/users/${user.id}/contracts`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch contracts: ${response.status}`);
      }
      
      const data = await response.json();
      setAllContracts(data);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      setError('Failed to load contracts for assignment.');
    } finally {
      setContractsLoading(false);
    }
  };

  // Fetch user's assigned contracts
  const fetchUserContracts = async (userId) => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
      const response = await fetch(`${apiBaseUrl}/api/users/${userId}/contracts`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch user contracts: ${response.status}`);
      }
      
      const data = await response.json();
      return data.map(contract => contract.contractId);
    } catch (error) {
      console.error('Error fetching user contracts:', error);
      return [];
    }
  };

  // Open contracts modal for a user
  const openContractsModal = async (userData) => {
    setSelectedUser(userData);
    const currentContracts = await fetchUserContracts(userData.id);
    setUserContracts(currentContracts);
    setShowContractsModal(true);
  };

  // Save contract assignments (called by the modal)
  const handleSaveContracts = async (contractIds) => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
      
      const response = await fetch(`${apiBaseUrl}/api/users/${selectedUser.id}/contracts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ contractIds })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update contract assignments: ${response.status}`);
      }
      
      // Success - close modal and reset state
      setShowContractsModal(false);
      setSelectedUser(null);
      setUserContracts([]);
      
      // Optionally refresh the users list or show a success message
      // fetchUsers(); // Uncomment if you want to refresh the full list
      
    } catch (error) {
      console.error('Error saving contract assignments:', error);
      throw error; // Re-throw so the modal can handle the error
    }
  };

  // Close contracts modal
  const handleCloseContractsModal = () => {
    setShowContractsModal(false);
    setSelectedUser(null);
    setUserContracts([]);
  };
  
  // Filter users based on selected filters and format for table
  const getFilteredAndFormattedUsers = () => {
    return users
      .filter(user => {
        if (filters.role !== 'all' && user.role !== filters.role) return false;
        if (filters.status !== 'all' && user.status !== filters.status) return false;
        return true;
      })
      .map(item => ({
        email: item.email,
        status: item.status,
        role: item.role,
        createdDate: formatDate(item.source === 'invitation' ? item.invited_at : item.created_at),
        updatedDate: formatDate(item.updated_at),
        lastLogin: item.source === 'invitation' ? null : formatDate(item.last_login),
        actionsComponent: (
          <div style={{ display: 'flex', gap: '8px' }}>
            {item.source === 'profile' && (
              <>
                <button
                  className="action-button primary-button"
                  onClick={() => openRoleModal(item)}
                  title="Change Role"
                >
                  Role
                </button>
                <button
                  className="action-button warning-button"
                  onClick={() => openStatusModal(item)}
                  title={item.status === 'disabled' ? 'Enable User' : 'Disable User'}
                >
                  {item.status === 'disabled' ? 'Enable' : 'Disable'}
                </button>
                {item.role === 'client' && (
                  <button
                    className="action-button secondary-button"
                    onClick={() => openContractsModal(item)}
                    title="Manage Contract Access"
                  >
                    Contracts
                  </button>
                )}
                <button
                  className="action-button danger-button"
                  onClick={() => handleDelete(item.id)}
                  title="Delete User"
                >
                  Delete
                </button>
              </>
            )}
            
            {item.source === 'invitation' && (
              <>
                <button
                  className="action-button warning-button"
                  onClick={() => handleRevokeInvitation(item.id)}
                  title="Revoke Invitation"
                >
                  Revoke
                </button>
                <button
                  className="action-button danger-button"
                  onClick={() => handleDeleteInvitation(item.id)}
                  title="Delete Invitation"
                >
                  Delete
                </button>
              </>
            )}
          </div>
        ),
        // Keep original data for actions
        id: item.id,
        source: item.source
      }));
  };

  // Fetch contracts for invitation modal
  const fetchInviteContracts = useCallback(async () => {
    try {
      setInviteContractsLoading(true);
      
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
      const response = await fetch(`${apiBaseUrl}/api/contracts`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch contracts: ${response.status}`);
      }
      
      const data = await response.json();
      setInviteContracts(data);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      setError('Failed to load contracts. Please try again later.');
    } finally {
      setInviteContractsLoading(false);
    }
  }, []);

  // Load contracts when invitation modal opens and role is client
  useEffect(() => {
    if (showInviteModal && newInvitation.role === 'client') {
      fetchInviteContracts();
    }
  }, [showInviteModal, newInvitation.role, fetchInviteContracts]);

  // Handle form input changes for invitation
  const handleInviteInputChange = (e) => {
    const { name, value } = e.target;
    setNewInvitation(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Reset contract selection if role changes away from client
    if (name === 'role' && value !== 'client') {
      setSelectedInviteContractIds(new Set());
    }
  };

  // Handle contract selection toggle for invitations
  const handleInviteContractToggle = (contractId) => {
    const newSelected = new Set(selectedInviteContractIds);
    if (newSelected.has(contractId)) {
      newSelected.delete(contractId);
    } else {
      newSelected.add(contractId);
    }
    setSelectedInviteContractIds(newSelected);
  };

  // Handle select all contracts for invitations
  const handleSelectAllInviteContracts = () => {
    const filteredContracts = getFilteredInviteContracts();
    if (selectedInviteContractIds.size === filteredContracts.length && filteredContracts.length > 0) {
      // Deselect all
      setSelectedInviteContractIds(new Set());
    } else {
      // Select all filtered contracts
      setSelectedInviteContractIds(new Set(filteredContracts.map(c => c.contractId)));
    }
  };

  // Filter contracts based on search term for invitations
  const getFilteredInviteContracts = () => {
    return inviteContracts.filter(contract => {
      if (!inviteContractSearchTerm) return true;
      
      const searchLower = inviteContractSearchTerm.toLowerCase();
      const contractName = contract.contractName || '';
      const contractNumber = contract.contractNumber || '';
      const businessUnit = contract.businessUnit || '';
      
      return contractName.toLowerCase().includes(searchLower) || 
             contractNumber.toLowerCase().includes(searchLower) ||
             businessUnit.toLowerCase().includes(searchLower);
    }).sort((a, b) => {
      const nameA = (a.contractName || '').toLowerCase();
      const nameB = (b.contractName || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  };

  // Handle form input changes for role update
  const handleRoleInputChange = (e) => {
    const { name, value } = e.target;
    setRoleUpdate(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form input changes for status update
  const handleStatusInputChange = (e) => {
    const { name, value } = e.target;
    setStatusUpdate(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle invitation form submission
  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      if (!newInvitation.email) {
        setError('Email is required');
        setLoading(false);
        return;
      }

      // Validate contract selection for client role
      if (newInvitation.role === 'client' && selectedInviteContractIds.size === 0) {
        setError('Please select at least one contract for client users');
        setLoading(false);
        return;
      }
      
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
      
      // Create the invitation
      const invitationData = {
        ...newInvitation,
        contractIds: newInvitation.role === 'client' ? Array.from(selectedInviteContractIds) : undefined
      };
      
      const response = await fetch(`${apiBaseUrl}/api/invitations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(invitationData)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to create invitation: ${response.status}`);
      }
      
      // Close modal and refresh user list
      setShowInviteModal(false);
      fetchUsers();
      
      // Reset form
      setNewInvitation({
        email: '',
        role: 'client'
      });
      setSelectedInviteContractIds(new Set());
      setInviteContractSearchTerm('');
    } catch (error) {
      console.error('Error creating invitation:', error);
      setError(`Failed to create invitation: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle role update form submission
  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!selectedUser) return;
      
      setLoading(true);
      
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
      const response = await fetch(`${apiBaseUrl}/api/users/${selectedUser.id}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(roleUpdate)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update role: ${response.status}`);
      }
      
      // Close modal and refresh user list
      setShowRoleModal(false);
      fetchUsers();
      // Reset selected user
      setSelectedUser(null);
    } catch (error) {
      console.error('Error updating role:', error);
      setError(`Failed to update role: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle status update form submission
  const handleStatusSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!selectedUser) return;
      
      setLoading(true);
      
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
      const response = await fetch(`${apiBaseUrl}/api/users/${selectedUser.id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(statusUpdate)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update status: ${response.status}`);
      }
      
      // Close modal and refresh user list
      setShowStatusModal(false);
      fetchUsers();
      // Reset selected user
      setSelectedUser(null);
    } catch (error) {
      console.error('Error updating status:', error);
      setError(`Failed to update status: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle user deletion
  const handleDelete = async (id) => {
    try {
      if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
        return;
      }
      
      setLoading(true);
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
      const response = await fetch(`${apiBaseUrl}/api/users/${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to delete user: ${response.status}`);
      }
      
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      setError(`Failed to delete user: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle invitation deletion
  const handleDeleteInvitation = async (id) => {
    try {
      if (!window.confirm('Are you sure you want to delete this invitation?')) {
        return;
      }
      
      setLoading(true);
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
      const response = await fetch(`${apiBaseUrl}/api/invitations/${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to delete invitation: ${response.status}`);
      }
      
      fetchUsers();
    } catch (error) {
      console.error('Error deleting invitation:', error);
      setError(`Failed to delete invitation: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle invitation revocation
  const handleRevokeInvitation = async (id) => {
    try {
      if (!window.confirm('Are you sure you want to revoke this invitation?')) {
        return;
      }
      
      setLoading(true);
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
      const response = await fetch(`${apiBaseUrl}/api/invitations/${id}/revoke`, {
        method: 'PUT'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to revoke invitation: ${response.status}`);
      }
      
      fetchUsers();
    } catch (error) {
      console.error('Error revoking invitation:', error);
      setError(`Failed to revoke invitation: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Open role modal for a user
  const openRoleModal = (user) => {
    setSelectedUser(user);
    setRoleUpdate({ role: user.role });
    setShowRoleModal(true);
  };

  // Open status modal for a user
  const openStatusModal = (user) => {
    setSelectedUser(user);
    setStatusUpdate({ status: user.status === 'disabled' ? 'active' : 'disabled' });
    setShowStatusModal(true);
  };

  return (
    <MainLayout title="User Management">
      <div className="scrollable-content">
        {/* Page Header */}
        <div className="page-header" style={{ padding: '0 25px', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>Manage Users</h2>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message" style={{ color: 'red', margin: '0 25px 20px', padding: '10px', backgroundColor: '#ffe6e6', borderRadius: '4px' }}>
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="filters-row" style={{ paddingLeft: '25px', marginTop: '20px' }}>
          <div className="filters-container">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <CustomSelect
                  label="Role"
                  name="role"
                  value={filters.role}
                  onChange={handleFilterChange}
                  options={roleOptions}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomSelect
                  label="Status"
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  options={statusOptions}
                />
              </Grid>
            </Grid>
          </div>
        </div>

        {/* Users Table */}
        <div className="contracts-table-container" style={{ paddingLeft: '25px', marginTop: '20px' }}>
          {loading && users.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>Loading users...</Box>
          ) : (
            <SortablePaginatedTable
              data={getFilteredAndFormattedUsers()}
              columns={userColumns}
              rowsPerPage={15}
              emptyMessage="No users found with the selected filters."
              tableContainerProps={{ className: "enhanced-table" }}
              tableProps={{ stickyHeader: true }}
              stickyColumn="email"
            />
          )}
        </div>

        {/* Add User Button */}
        <div className="contracts-actions" style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 25px', marginTop: '20px' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setShowInviteModal(true)}
            className="add-contract-button"
          >
            Invite User
          </Button>
        </div>
      </div>

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className={`modal ${newInvitation.role === 'client' ? 'large' : ''}`} onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-header">Invite New User</h2>
            <p>Enter the email address of the user you would like to invite.</p>
            
            <form onSubmit={handleInviteSubmit}>
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="form-control"
                  value={newInvitation.email}
                  onChange={handleInviteInputChange}
                  required
                  autoFocus
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="role">Role</label>
                <select
                  id="role"
                  name="role"
                  className="form-control"
                  value={newInvitation.role}
                  onChange={handleInviteInputChange}
                >
                  <option value="admin">Admin</option>
                  <option value="client">Client</option>
                  <option value="team_member">Team Member</option>
                </select>
              </div>

              {/* Contract Selection - Only show for client role */}
              {newInvitation.role === 'client' && (
                <div className="form-group">
                  <label>Contract Access ({selectedInviteContractIds.size} selected)</label>
                  <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                    Select which contracts this client can access. You can search to find specific contracts.
                  </p>
                  
                  {/* Search input */}
                  <input
                    type="text"
                    placeholder="Search by contract name, number, or business unit..."
                    value={inviteContractSearchTerm}
                    onChange={(e) => setInviteContractSearchTerm(e.target.value)}
                    className="form-control"
                    style={{ marginBottom: '10px' }}
                  />

                  {/* Contract List */}
                  <div style={{ 
                    maxHeight: '300px', 
                    overflowY: 'auto', 
                    border: '1px solid #ddd', 
                    borderRadius: '4px'
                  }}>
                    {inviteContractsLoading ? (
                      <div style={{ textAlign: 'center', padding: '20px' }}>
                        Loading contracts...
                      </div>
                    ) : inviteContracts.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                        No contracts available
                      </div>
                    ) : (
                      <>
                        {/* Select All button */}
                        <div style={{ marginBottom: '10px', padding: '10px', borderBottom: '1px solid #eee' }}>
                          <button
                            type="button"
                            onClick={handleSelectAllInviteContracts}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#8A42EC',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}
                          >
                            {selectedInviteContractIds.size === getFilteredInviteContracts().length && getFilteredInviteContracts().length > 0 
                              ? 'Deselect All' 
                              : 'Select All'
                            } ({getFilteredInviteContracts().length})
                          </button>
                        </div>

                        {/* Contracts Table */}
                        <table className="invitations-table" style={{ margin: 0, boxShadow: 'none' }}>
                          <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}>
                            <tr>
                              <th style={{ width: '40px' }}>
                                <input 
                                  type="checkbox" 
                                  checked={selectedInviteContractIds.size === getFilteredInviteContracts().length && getFilteredInviteContracts().length > 0}
                                  onChange={handleSelectAllInviteContracts}
                                  title={selectedInviteContractIds.size === getFilteredInviteContracts().length ? 'Deselect all' : 'Select all'}
                                />
                              </th>
                              <th>Contract Name</th>
                              <th>Contract Number</th>
                              <th>Business Unit</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getFilteredInviteContracts().map((contract) => {
                              const isSelected = selectedInviteContractIds.has(contract.contractId);
                              return (
                                <tr 
                                  key={contract.contractId} 
                                  onClick={() => handleInviteContractToggle(contract.contractId)}
                                  className={isSelected ? 'selected-row' : ''}
                                  style={{ 
                                    cursor: 'pointer',
                                    backgroundColor: isSelected ? '#f0e6ff' : 'inherit'
                                  }}
                                >
                                  <td>
                                    <input 
                                      type="checkbox" 
                                      checked={isSelected}
                                      onChange={() => handleInviteContractToggle(contract.contractId)}
                                    />
                                  </td>
                                  <td style={{ fontWeight: isSelected ? 'bold' : 'normal' }}>
                                    {contract.contractName || 'N/A'}
                                  </td>
                                  <td>{contract.contractNumber || 'N/A'}</td>
                                  <td>{contract.businessUnit || 'N/A'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>

                        {getFilteredInviteContracts().length === 0 && inviteContractSearchTerm && (
                          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                            No contracts found matching "{inviteContractSearchTerm}"
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="action-button" 
                  onClick={() => {
                    setShowInviteModal(false);
                    setSelectedInviteContractIds(new Set());
                    setInviteContractSearchTerm('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="action-button primary-button"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Update Role Modal */}
      {showRoleModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal">
            <h2 className="modal-header">Update User Role</h2>
            <p>Update the role for {selectedUser.email}</p>
            
            <form onSubmit={handleRoleSubmit}>
              <div className="form-group">
                <label htmlFor="role-update">Role</label>
                <select
                  id="role-update"
                  name="role"
                  className="form-control"
                  value={roleUpdate.role}
                  onChange={handleRoleInputChange}
                >
                  <option value="admin">Admin</option>
                  <option value="client">Client</option>
                  <option value="team_member">Team Member</option>
                </select>
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="action-button" 
                  onClick={() => setShowRoleModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="action-button primary-button"
                  disabled={loading}
                >
                  {loading ? 'Updating...' : 'Update Role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Update Status Modal */}
      {showStatusModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal">
            <h2 className="modal-header">Update User Status</h2>
            <p>Update the status for {selectedUser.email}</p>
            
            <form onSubmit={handleStatusSubmit}>
              <div className="form-group">
                <label htmlFor="status-update">Status</label>
                <select
                  id="status-update"
                  name="status"
                  className="form-control"
                  value={statusUpdate.status}
                  onChange={handleStatusInputChange}
                >
                  <option value="active">Active</option>
                  <option value="disabled">Disabled</option>
                </select>
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="action-button" 
                  onClick={() => setShowStatusModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="action-button primary-button"
                  disabled={loading}
                >
                  {loading ? 'Updating...' : 'Update Status'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Contract Assignment Modal - New improved version */}
      <ContractAssignmentModal
        open={showContractsModal}
        onClose={handleCloseContractsModal}
        onSave={handleSaveContracts}
        selectedUser={selectedUser}
        initialSelectedContracts={userContracts}
      />
    </MainLayout>
  );
};

export default UsersPage;